import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CustomBreadCrumb } from '@/components/customBreadCrumb';
import './style.scss';
import {
  CustomInput,
  CustomPagination,
  CustomSwitch,
  CustomTable,
  PaginationText,
} from '@/components';
import { Edit, Info } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CategoryType, UserType } from '@/DTO';
import { useAppPagination } from '@/Router/hook';
import { CategoryApi } from '@/api';
import { useAppSelector } from '@/redux-config/hooks';
import { selectUser } from '@/redux-config/entities/user';
import { InsideLoading } from '@/components/loading/inside';
import { selectLanguage } from '@/redux-config/entities/language';
import { ConvertDate, ConvertNumber } from '@/utils';
import { SearchComponent } from '@/components/search';
import Swal from 'sweetalert2';
import { CustomModal } from '@/components/custom-modal';

export const CategoryChildren: React.FC<{}> = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const crumbs = [
    { name: t('Dashboard'), href: '/' },
    { name: t('Category'), href: '#' },
    { name: t('List'), href: '#' },
  ];

  const navigate = useNavigate();
  const lang = useAppSelector(selectLanguage);
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState<boolean>(true);

  const [data, setData] = useState<CategoryType>();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalDataID, setModalDataID] = useState<string>();
  const [modalDataText, setModalDataText] = useState<string>('');

  const [showModalNew, setShowModalNew] = useState<boolean>(false);
  const [modalNewDataText, setModalNewDataText] = useState<string>('');

  const handleGetData = () => {
    setLoading(true);
    CategoryApi.getCategory(user, id + '')
      .then((data) => {
        setData(data.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    handleGetData();
  }, [id]);

  const handleCreateCategory = () => {
    CategoryApi.NewCategory(user, modalNewDataText, id + '')
      .then(() => {
        setShowModalNew(false);
        setModalNewDataText('');
        handleGetData();
      })
      .catch((err) => {
        setShowModalNew(false);
      });
  };

  const handleEditCategory = () => {
    CategoryApi.update(user, modalDataID + '', modalDataText)
      .then(() => {
        setShowModal(false);
        setModalDataText('');
        handleGetData();
      })
      .catch((err) => {
        setShowModal(false);
      });
  };

  const handleChangeStatus = (id: string) => {
    CategoryApi.ChangeStatus(user, id + '')
      .then((data) => {
        Swal.fire({
          text: 'User status has been changed',
          icon: 'success',
        });
      })
      .catch((e) => {});
  };

  return (
    <>
      <Helmet>
        <title>MahdiBookShop | Category List</title>
        <meta name="description" content="Basic example" />
      </Helmet>
      <div className="d-flex flex-column">
        <CustomBreadCrumb breadCrumbList={crumbs} />
        <div className="w-100 p-2">
          <div className="rounded-3 py-2 bg-custom-white h-100 d-flex flex-column justify-content-between">
            <div
              className={
                'd-flex flex-row gap-2 align-items-center justify-content-between p-3'
              }
            >
              <div className={'d-flex flex-row align-items-center'}>
                <h5>
                  {t('Category')} {data?.name}
                </h5>
              </div>
              <div className={'d-flex flex-row gap-2'}>
                <Link
                  to={`/product/new?${
                    'categoryId=' + id + '&categoryName=' + data?.name
                  }`}
                  onClick={() => setShowModalNew(true)}
                  className="prm-btn rounded-3 mx-1 text-nowrap"
                >
                  <span>{t('NewProduct')}</span>
                </Link>
                <button
                  onClick={() => setShowModalNew(true)}
                  className="prm-btn rounded-3 mx-1 text-nowrap"
                >
                  <span>{t('New')}</span>
                </button>
              </div>
            </div>
            {loading ? (
              <InsideLoading />
            ) : (
              <>
                <CustomTable
                  header={[
                    <span>{t('No')}</span>,
                    <span>{t('Name')}</span>,
                    <span>{t('RegisterDate')}</span>,
                    <span>{t('status')}</span>,
                    <span>{t('Options')}</span>,
                  ]}
                  items={
                    data?.children
                      ? [
                          ...data?.children?.map((item, key) => [
                            <span>{ConvertNumber(key + 1 + '', lang)}</span>,
                            <span>{item.name}</span>,
                            <span>
                              {ConvertDate(item.created_at + '', lang)}
                            </span>,
                            <CustomSwitch
                              defaultChecked={item.status === 'active'}
                              onClick={() => {
                                handleChangeStatus(item.id);
                              }}
                            />,
                            <div
                              className={
                                'd-flex flex-row gap-1 justify-content-center'
                              }
                            >
                              <Link
                                to={`/category/children/${item.id}`}
                                className={
                                  'btn p-1 align-baseline rounded-circle bg-custom-light'
                                }
                              >
                                <Info />
                              </Link>
                              <button
                                className="btn p-1 align-baseline rounded-circle bg-custom-light"
                                onClick={() => {
                                  setShowModal(true);
                                  setModalDataID(item.id);
                                  setModalDataText(item.name);
                                }}
                              >
                                <Edit />
                              </button>
                            </div>,
                          ]),
                        ]
                      : []
                  }
                />
              </>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={modalDataText}
      >
        <div className={'d-flex flex-column gap-3'}>
          <label className={'d-flex flex-column'}>
            <span>{t('Name')}</span>
            <CustomInput
              type={'text_number'}
              name={'name of category'}
              className={'custom-input border-reg'}
              value={modalDataText}
              onChange={setModalDataText}
            />
          </label>

          <button
            className={'prm-btn rounded-3 align-self-start'}
            onClick={() => {
              if (modalDataID) {
                handleEditCategory();
              }
            }}
          >
            {t('submit')}
          </button>
        </div>
      </CustomModal>

      <CustomModal
        showModal={showModalNew}
        setShowModal={setShowModalNew}
        title={t('new') + ''}
      >
        <div className={'d-flex flex-column gap-3'}>
          <label className={'d-flex flex-column'}>
            <span>{t('Name')}</span>
            <CustomInput
              type={'text_number'}
              name={'name of category'}
              className={'custom-input border-reg'}
              value={modalNewDataText}
              onChange={setModalNewDataText}
            />
          </label>
          <button
            className={'prm-btn rounded-3 align-self-start'}
            onClick={() => {
              handleCreateCategory();
            }}
          >
            {t('submit')}
          </button>
        </div>
      </CustomModal>
    </>
  );
};
